import { compactNumber } from '@/utilities/Number';
import { DateTime } from 'luxon';

export interface ProgramRate {
	programID: number
	programName?: string
	programType?: string
	accountID: number
	accountName: number
	topLevelIndustry?: string
	coverageTypeID: number
	coverageType?: string
	programStart?: string
	programEnd?: string
	coveragePremium?: number
	previousCoveragePremium?: number
	premiumChange?: number
	coverageRetention?: number
	previousCoverageRetention?: number
	retentionChange?: number
	coverageAggLimit?: number
	previousCoverageAggLimit?: number
	aggLimitChange?: number
	coverageOccLimit?: number
	previousCoverageOccLimit?: number
	occLimitChange?: number
	aggRate?: number
	previousAggRate?: number
	aggRateChange?: number
	occRate?: number
	previousOccRate?: number
	occRateChange?: number
	exposureMetricID?: number
	exposureCategory?: string
	exposureType?: string
	exposureValue?: number
	previousExposureValue?: number
	exposureChange?: number
	exposureEffective?: string
	previousExposureEffective?: string
	unitScale?: number
}

export interface ProgramRatePeriod extends ProgramRate {
  rate: string
  rateChange: number | undefined
  year: string
}


export interface ProgramRateGroup {
  coverageType: string
  exposureName?: string
  exposureType: string
  showExposure: boolean
  periods: ProgramRatePeriod[]
}

export function groupedProgramRates(programRates: ProgramRate[]): ProgramRateGroup[] {
      const coverageTypeGroups = new Map<string, ProgramRateGroup>();

      for (const pr of programRates) {
        const key = `${pr.coverageType}${pr.exposureType || ''}`;

        if (!key) continue;

        if (!coverageTypeGroups.has(key)) {
          const exposureLabel = ((): string | undefined => {
            switch (pr.exposureCategory) {
              case pr.coverageType: return pr.exposureType;
              case pr.exposureType: return pr.exposureCategory;
              default: return `${pr.exposureCategory}: ${pr.exposureType}`;
            }
          })();

          coverageTypeGroups.set(key, {
            coverageType: pr.coverageType || '',
            exposureName: exposureLabel,
            exposureType: pr.exposureType || '',
            showExposure: !!pr.exposureCategory,
            periods: []
          });
        }

        const [coveragePremium, exposureValue, unitScale, previousCoveragePremium, previousExposureValue] = [
          (pr.coveragePremium || 0),
          (pr.exposureValue || 0),
          (pr.unitScale ?? 1),
          (pr.previousCoveragePremium || 0),
          (pr.previousExposureValue || 0)
        ];

        const rate = (coveragePremium / exposureValue * unitScale);
        const prevRate = (previousCoveragePremium / previousExposureValue * unitScale);

        const rateLabel = compactNumber(rate);
        const rateChangeLabel = !!rate && !!prevRate ? (rate - prevRate) / prevRate : undefined;

        const dt = (str: string): string => DateTime.fromISO(str).toLocaleString({month: 'numeric', day: 'numeric', year: '2-digit', timeZone: 'UTC'});

        coverageTypeGroups.get(key)?.periods.push({
          ...pr,
          year: `${dt(pr.programStart || '')} - ${dt(pr.programEnd || '')}`,
          rate: `${rateLabel === '--' ? '' : '$'}${rateLabel}${rateLabel === '--' ? '' : ` / ${compactNumber(pr.unitScale)}`}`,
          rateChange: rateChangeLabel
        });
      }
      const groups = Array.from(coverageTypeGroups.values());
      groups.forEach(group => group.periods.sort((a, b) => (Number(a.year).valueOf() - Number(b.year).valueOf())));
      groups.sort((a, b) => a.coverageType.localeCompare(b.coverageType));

      return groups;
}

